"use client";
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import katex from 'katex';
import { preprocessMathContent } from '@/utils/mathRenderer';

export interface MathExtensionOptions {
  placeholder?: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    math: {
      insertMath: (content: string) => ReturnType;
    };
  }
}

export const MathExtension = Extension.create<MathExtensionOptions>({
  name: 'math',

  addOptions() {
    return {
      placeholder: 'Enter LaTeX math...',
    };
  },

  addCommands() {
    return {
      insertMath: (content: string) => ({ commands }) => {
        return commands.insertContent(`\\(${content}\\)`);
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('math'),
        props: {
          decorations: (state) => {
            const { doc } = state;
            const decorations: Decoration[] = [];
            const mathRegex = /\\\(([\s\S]*?)\\\)/g;

            doc.descendants((node, pos) => {
              if (node.isText) {
                let match;
                while ((match = mathRegex.exec(node.text!)) !== null) {
                  const start = pos + match.index;
                  const end = start + match[0].length;
                  
                  try {
                    const processedMath = preprocessMathContent(match[1]);
                    const renderedMath = katex.renderToString(processedMath, {
                      throwOnError: false,
                      displayMode: false,
                      output: 'html',
                    });

                    decorations.push(
                      Decoration.widget(start, () => {
                        const span = document.createElement('span');
                        span.innerHTML = renderedMath;
                        span.className = 'math-inline';
                        return span;
                      }),
                      Decoration.widget(end, () => {
                        return document.createTextNode('');
                      })
                    );
                  } catch (error) {
                    console.warn('Failed to render math:', match[1], error);
                  }
                }
              }
              return true;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});
